import { Field } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import { EmailTemplate } from '../../EmailTemplates/components/hooks/useTemplates';

interface SharingSettingsProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    templates: EmailTemplate[];
    selectedTemplate: EmailTemplate | null;
    setSelectedTemplate: (template: EmailTemplate | null) => void;
}

export default function SharingSettings({
    values,
    setFieldValue,
    templates,
    selectedTemplate,
    setSelectedTemplate,
}: SharingSettingsProps) {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-base font-semibold text-foreground">
                    Sharing Settings
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                    Configure how users can share photos and the messaging
                    experience.
                </p>
            </div>

            {/* Sharing Options */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Sharing Options
                    </h4>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="sendLinkToPhoto"
                            name="sendLinkToPhoto"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="sendLinkToPhoto"
                            className="text-sm font-medium"
                        >
                            Send link to individual photo
                        </Label>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="showGalleryLink"
                            name="showGalleryLink"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="showGalleryLink"
                            className="text-sm font-medium"
                        >
                            Show gallery link in sharing
                        </Label>
                    </div>
                </div>
            </div>

            {/* Text Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Text Sharing
                    </h4>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="textEnabled"
                            name="textEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="textEnabled"
                            className="text-sm font-medium"
                        >
                            Enable text sharing
                        </Label>
                    </div>
                </div>

                {values.textEnabled && (
                    <div className="space-y-2">
                        <Label htmlFor="textBody">Text Message</Label>
                        <Field
                            as={Textarea}
                            id="textBody"
                            name="textBody"
                            rows={4}
                            placeholder="Enter the default text message for sharing"
                        />
                    </div>
                )}
            </div>

            {/* WhatsApp Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        WhatsApp Sharing
                    </h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Enable WhatsApp sharing for international guests.
                    </p>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="whatsappEnabled"
                            name="whatsappEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="whatsappEnabled"
                            className="text-sm font-medium"
                        >
                            Enable WhatsApp sharing
                        </Label>
                    </div>
                </div>
            </div>

            {/* Email Sharing */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Email Sharing
                    </h4>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Field
                            id="emailEnabled"
                            name="emailEnabled"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                        />
                        <Label
                            htmlFor="emailEnabled"
                            className="text-sm font-medium"
                        >
                            Enable email sharing
                        </Label>
                    </div>
                </div>

                {values.emailEnabled && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="fromAddress">
                                From Email Address
                            </Label>
                            <Field
                                as={Input}
                                id="fromAddress"
                                name="fromAddress"
                                type="email"
                                placeholder="noreply@example.com"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="fromName">From Name</Label>
                            <Field
                                as={Input}
                                id="fromName"
                                name="fromName"
                                placeholder="Your Gallery"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="subject">Email Subject</Label>
                            <Field
                                as={Input}
                                id="subject"
                                name="subject"
                                placeholder="You have new photos!"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendAttachments"
                                    name="sendAttachments"
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded"
                                />
                                <Label
                                    htmlFor="sendAttachments"
                                    className="text-sm font-medium"
                                >
                                    Send Attachments
                                </Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="emailBody">
                                        Email Body
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <select
                                            className="h-9 text-sm border border-input rounded-md bg-transparent px-3 py-1"
                                            onChange={(e) =>
                                                setSelectedTemplate(
                                                    templates.find(
                                                        (template) =>
                                                            template.id ===
                                                            +e.currentTarget
                                                                .value,
                                                    ) ?? null,
                                                )
                                            }
                                        >
                                            <option value="">
                                                Select template
                                            </option>
                                            {templates.map((template) => (
                                                <option
                                                    key={template.id}
                                                    value={template.id}
                                                >
                                                    {template.name}
                                                </option>
                                            ))}
                                        </select>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (selectedTemplate) {
                                                    setFieldValue(
                                                        'emailBody',
                                                        selectedTemplate?.body,
                                                    );
                                                }
                                            }}
                                        >
                                            Apply Template
                                        </Button>
                                    </div>
                                </div>
                                <div className="border border-border rounded-md overflow-hidden">
                                    <Editor
                                        value={values.emailBody}
                                        onValueChange={(code) =>
                                            setFieldValue('emailBody', code)
                                        }
                                        highlight={(code) =>
                                            Prism.highlight(
                                                code,
                                                Prism.languages.html,
                                                'html',
                                            )
                                        }
                                        padding={10}
                                        style={{
                                            fontFamily:
                                                '"Fira code", "Fira Mono", monospace',
                                            fontSize: 14,
                                            minHeight: '200px',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Share Description */}
            <div className="space-y-4">
                <div>
                    <h4 className="text-md font-medium text-foreground">
                        Share Description
                    </h4>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="createShareDescriptionText">
                        Description Text
                    </Label>
                    <Field
                        as={Input}
                        id="createShareDescriptionText"
                        name="createShareDescriptionText"
                        placeholder="Enter description text for sharing"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label htmlFor="createShareDescriptionTextSize">
                            Font Size
                        </Label>
                        <Field
                            as={Input}
                            id="createShareDescriptionTextSize"
                            name="createShareDescriptionTextSize"
                            type="number"
                            placeholder="16"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="createShareDescriptionTextFont">
                            Font Family
                        </Label>
                        <Field
                            as={Input}
                            id="createShareDescriptionTextFont"
                            name="createShareDescriptionTextFont"
                            placeholder="Arial, sans-serif"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
